import notify from 'devextreme/ui/notify';
import { ExternalUserRole } from '../../models/ExternalUserRole';
import { PermissionType } from '../../dtos/PermissionType';
import { PhaseAction } from '../../dtos/PhaseAction';
import { PhaseDto } from '../../dtos/PhaseDto';
import { PhasePermissionConfig } from '../../dtos/PhasePermissionConfig';
import { PhaseStatus } from '../../dtos/PhaseStatus';
import { PhaseWorkflowConfig } from '../../dtos/PhaseWorkflowConfig';
import { PhaseWorkflowType } from '../../dtos/PhaseWorkflowType';
import { StepDto } from '../../dtos/StepDto';
import { StepPermissionConfig } from '../../dtos/StepPermissionConfig';
import { StepStatus } from '../../dtos/StepStatus';
import { StepTransitionConfig } from '../../dtos/StepTransitionConfig';
import { StepWorkflowConfig } from '../../dtos/StepWorkflowConfig';
import { StepWorkflowType } from '../../dtos/StepWorkflowType';
import { DefaultPhaseDto } from '../../dtos/DefaultPhaseDto';
import { DefaultStepDto } from '../../dtos/DefaultStepDto';
import { UserRole } from '../../dtos/UserRole';
import { Modules } from '../../dtos/Modules';
import { IAppContext } from './AppContext';
import { StatusInfo } from './Interfaces';
import { GRANT_DEFAULTS, NONE_WORKFLOW_DEFAULTS, TEMPLATE_DEFAULTS, TasksFeatureFlags } from './Types';
import { TasklistMode } from './tasklist/context/TaskListContext';


//TODO this stuff should be part of DTO generation
export const ROLE = {
    ORF_DTR: 0,
    PI: 1,
    PO: 2,
    AOR: 3,
    ORF_DEP: 5,
    PO_SUPERVISOR: 10,
    GMS_SUPERVISOR: 11,
    ORF_DTR_SUPERVISOR: 12,
    EXTERNAL_USER: 100,
    SU: 999,
}

export const EXTERNALUSERROLE = {
    REVIEWER: 3,
    AE_DESIGNER: 4,
    PROJECT_MANAGER: 5
}

// ============================================================================
// ROLE MAPPING HELPERS
// These functions map between UserRole and ExternalUserRole using +100 offset.
// ============================================================================

const EXTERNAL_ROLE_OFFSET = 100;

/**
 * Returns true if the role is an external role (EXTERNAL_REVIEWER, EXTERNAL_AE_DESIGNER, EXTERNAL_PROJECT_MANAGER).
 */
export function isExternalRole(role: UserRole): boolean {
    return role === UserRole.EXTERNAL_REVIEWER
        || role === UserRole.EXTERNAL_AE_DESIGNER
        || role === UserRole.EXTERNAL_PROJECT_MANAGER;
}

/**
 * Maps an ExternalUserRole to its corresponding UserRole using +100 offset.
 * Example: ExternalUserRole.REVIEWER (3) -> UserRole.EXTERNAL_REVIEWER (103)
 */
export function externalRoleToUserRole(externalRole: ExternalUserRole): UserRole {
    return (externalRole + EXTERNAL_ROLE_OFFSET) as UserRole;
}

/**
 * Gets the effective UserRole for permission checking.
 * For internal users, returns their role directly.
 * For external users (EXTERNAL_USER), resolves their grant-specific ExternalUserRole
 * and converts it to the corresponding UserRole (e.g., PROJECT_MANAGER -> EXTERNAL_PROJECT_MANAGER).
 *
 * @param app - The app context containing user info and externalUserRoles
 * @param grantId - The grant ID to look up the external user's role for
 * @returns The effective UserRole for permission checking, or null if external user has no role for this grant
 */
export function getEffectiveUserRole(app: IAppContext, grantId: string | null | undefined): UserRole | null {
    // For internal users, return their role directly
    if (app.userRole !== ROLE.EXTERNAL_USER) {
        return app.userRole as UserRole;
    }

    // For external users, look up their role for this specific grant
    if (!grantId || !app.externalUserRoles || !app.externalUserRoles[grantId]) {
        return null;
    }

    const grantRoles = app.externalUserRoles[grantId];
    if (!Array.isArray(grantRoles) || grantRoles.length === 0) {
        return null;
    }

    // Get the role from the permissions object (set by backend in UserService.RetrieveExternalUserPermissions)
    const permissions = grantRoles[0];

    // Check for explicit role property first
    if (permissions?.role !== undefined && permissions?.role !== null) {
        return externalRoleToUserRole(permissions.role as ExternalUserRole);
    }

    // Fall back to checking externalUserAccess array for external roles (PM, Reviewer, A/E Designer)
    if (Array.isArray(permissions?.externalUserAccess)) {
        if (permissions.externalUserAccess.includes(ExternalUserRole.PROJECT_MANAGER)) {
            return UserRole.EXTERNAL_PROJECT_MANAGER;
        }
        if (permissions.externalUserAccess.includes(ExternalUserRole.REVIEWER)) {
            return UserRole.EXTERNAL_REVIEWER;
        }
        if (permissions.externalUserAccess.includes(ExternalUserRole.AE_DESIGNER)) {
            return UserRole.EXTERNAL_AE_DESIGNER;
        }
    }

    return null;
}

export const GRANTSTATUS = {
    AWARDED: 5,
    APPLICATION: 7,
    COUNCILREVIEW: 12,
    AWARDPREPARED: 19,
    TOBEPAID: 35
}

export const STATUS = {
    SUCCESS: "Success",
    SCHEDULED: "Scheduled",
    COMPLETED: "Completed",
    INPROGRESS: "In Progress",
    SLIGHTLYLATE: "Slightly Late",
    LATE: "Late"
}

export const FY = {
    ALL: "All FY",
    ACTIVE: "All Active"
}

export const TIMELINE = {
    W3SVGURL: "http://www.w3.org/2000/svg",
    FONTNAMES: "Helvetica Neue, Segoe UI, helvetica",
    BACKGROUNDCOLOR: "#ffffff",
    SVG: "div[id^=reactgooglegraph] > div > div > div > svg",
    LEGENDSVG: "div[id^=reactgooglegraph] > div > div > div > div > svg",
    CURRENTDATECOLOR: "blue",
    CURRENDATEFONTSIZE: "13",
    CURRENDATEFONTWEIGHT: "Bold",
    CURRENTDATEFONT: "Helvetica Neue, Segoe UI, helvetica",
    TOOLTIPSTYLE: "width: 170px; margin: 10px;",
    TOOLTIPFILL: "#4d4d4d",
    SCHEDULEDCOLOR: "#d3d3d3",
    COMPLETEDCOLOR: "#83d18e",
    INPROGRESSCOLOR: "#f2a600",
    SLIGHTLYLATECOLOR: "#ffff00",
    LATECOLOR: "#c80112",
    PHASEFILL: "#efefef",
    GRIDLINECOLOR1: "#ffffff",
    GRIDLINECOLOR2: "#b7b7b7",
    GRIDLINECOLOR3: "#e6e6e6",
    BORDERCOLOR: "#9a9a9a",
}

export const MESSAGES = {
    UPDATESSAVED: "Your updates have been saved.",
    EXITPAGECONFIRMATION: "You have unsaved changes. Are you sure you want to exit this page ?",
    NOLONGERLOGGEDIN: "You are no longer logged in to the system. Please re-login.",
    SERVERCANNOTPROCESSACTION: "Server cannot process your action. You may not be logged in to the system. Please re-login.",
    BADREQUEST: "Bad Request"
}

export const HTTP = {
    BADREQUEST: "Bad Request",
    HTTP_401: "HTTP error: 401",
    HTTP_500: "HTTP error: 500"
}

export const CLASSES = {
    DETAILSLOADING: "detailsLoadingClass",
    DETAILSLOADED: "detailsLoadedClass",
    DETAILSLOADEDTIMELINE: "detailsLoadedClass row grantDetailsRowTimeline",
    PHASESHOW: "showPhase",
    PHASEHIDE: "hidePhase"
}


export const DATES = {
    MINIMUM: "1/1/1900",
    MAXIMUM: "1/1/9999",
    FORMAT: "MM-DD-YYYY"
}

export const UPLOADFILETYPE = {
    TRIMBLE: 0,
    EXTERNALUSERS: 1
};

export const UPLOADSTATUS = {
    SUCCESS: 1,
    INVALIDDATA: 3
};




export const shortenDate = (inputDate: string) => {
    var formattedDate = inputDate;

    if (inputDate) {
        formattedDate = inputDate.substring(0, 10);
    }

    return formattedDate;
}


export const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
        year: '2-digit', month: '2-digit', day: '2-digit'
    }).format(date);
}


export const notification = (options: any, stack: any) => {
    if (!options.displayTime)
        options.displayTime = 5000;
    if (!options.width)
        options.width = '1200px';
    if (!stack)
        stack = { position: 'bottom center', direction: 'up-push' }

    notify(options, stack);
}

export const validateEmail = (email: string) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
}

export const handleError = (error: string) => {
    if (error == HTTP.HTTP_401) {
        return MESSAGES.NOLONGERLOGGEDIN
    }

    if (error == HTTP.HTTP_500) {
        return MESSAGES.SERVERCANNOTPROCESSACTION
    }

    if (error == HTTP.BADREQUEST) {
        return MESSAGES.BADREQUEST
    }
    return error;
}

export const getUserFullName = (user: any) => {

    var name = null;

    if (user) {

        if ((user.firstName && user.firstName.trim()) && (user.lastName && user.lastName.trim())) {
            name = `${user.firstName} ${user.lastName}`;
            name = name.trim();
        }
        else if (user.fullName && user.fullName.trim()) {
            name = user.fullName.trim();
        }

        if (!name) {
            name = user.emailAddress ?? user.email;
        }
    }

    return name;
}

const gray = "#B0B0B0";
const lightBlue = "#81D4FA";
const lightGreen = "#B3E5A1"
const blue = "#4285F4";
const red = "#E91E63";
const teal = "#00ACC1";
const green = "#34A853";

const white = "#FFFFFF";
const gold = "#FFD700";
const black = "#000000";

// Workflow types that use the plain status wording ("In Progress"/"Complete") instead of the
// NIH/grantee-cycle wording. Mirrors isSimpleStatusSet in TemplateTriggersTab.
export const isSimpleStatusWorkflow = (workflowType?: PhaseWorkflowType | null): boolean =>
    workflowType === PhaseWorkflowType.None;

// A document store rather than a workflow: its subtasks are folders holding files, with no
// status, due date, description or Mark as Complete, so nothing rolls up into the task. The DC
// document phases (SD/DD/CD/BS/RD) are seeded this way.
export const isUploadOnlyPhase = (phase?: PhaseDto | null): boolean =>
    phase?.defaultPhase?.settings?.uploadOnlySubtasks === true;

// The task status a header shows without the subtasks in hand: Step-style tasks read the newest
// cycle's server-derived rollup, everything else the stored status. Newest cycle only, matching the
// tasklist toolbar icon.
export const taskHeaderStatus = (phase: PhaseDto): PhaseStatus => {
    if (isStepStyleWorkflow(phase.defaultPhase?.settings?.workflowType)) {
        const newest = [...(phase.phaseSubmissions ?? [])]
            .sort((a, b) => (b.resubmission ?? 0) - (a.resubmission ?? 0))[0];
        return newest?.status ?? phase.status;
    }

    return phase.status;
};

export const getStepStatusInfo = (status: StepStatus, dueDate: Date | null, workflowType?: PhaseWorkflowType | null): StatusInfo => {

    switch (status) {
        case StepStatus.NOT_STARTED:
            return { text: "Not Started", color: gray };
        case StepStatus.SETUP:
            return { text: isSimpleStatusWorkflow(workflowType) ? "In Progress" : "In Progress - NIH", color: lightBlue };
        case StepStatus.READY:
            return { text: "Ready - NIH", color: lightGreen };
        case StepStatus.GRANTEE_PENDING:
            return getLateStatus(dueDate, { text: "In Progress - Grantee", color: blue });
        case StepStatus.PENDING_REVISION:
        case StepStatus.GRANTEE_PENDING_REVISION:
            return getLateStatus(dueDate, { text: "Need Information - Grantee", color: red });
        case StepStatus.GRANTEE_COMPLETE:
            return { text: "Complete - Grantee", color: lightGreen };
        case StepStatus.PENDING:
            return { text: "Pending Approval - NIH", color: teal };
        case StepStatus.APPROVED:
            return { text: isSimpleStatusWorkflow(workflowType) ? "Complete" : "Approved", color: green };
        case StepStatus.INACTIVE:
            return { text: "Inactive", color: gray, border: "1px solid " + gray };
        default:
            return { text: " *** invalid status *** ", color: black, border: "1px solid " + black };
    }
}

// New utility function to check if a phase/step is complete in None workflow
export const isNoneWorkflowComplete = (completionDate: Date | null): boolean => {
    return !!completionDate;
}

// Helper function to get the appropriate status info based on workflow type
export const getStepStatusInfoByWorkflow = (
    workflowType: PhaseWorkflowType | null | undefined,
    step: StepDto
): StatusInfo => {
    return getStepStatusInfo(step.status, step.dueDate, workflowType);
}

// Helper function to automatically set completion dates for Phase workflow when approved
export const updateStepWithCompletionLogic = (
    step: StepDto,
    newStatus: StepStatus,
    workflowType: PhaseWorkflowType | null | undefined
): StepDto => {
    // For Phase workflow, set completion date when approved (if not already set)
    if (workflowType === PhaseWorkflowType.Phase &&
        newStatus === StepStatus.APPROVED &&
        !step.completionDate) {
        return {
            ...step,
            status: newStatus,
            completionDate: new Date()
        };
    }

    return {
        ...step,
        status: newStatus
    };
}

export const getPhaseStatusInfo = (status: PhaseStatus, dueDate: Date | null, workflowType?: PhaseWorkflowType | null): StatusInfo => {

    switch (status) {
        case PhaseStatus.NOT_STARTED:
            return { text: "Not Started", color: gray };
        case PhaseStatus.SETUP:
            return { text: isSimpleStatusWorkflow(workflowType) ? "In Progress" : "In Progress - NIH", color: lightBlue };
        case PhaseStatus.READY:
            return { text: "Ready - NIH", color: lightGreen };
        case PhaseStatus.GRANTEE_PENDING:
            return getLateStatus(dueDate, { text: "In Progress - Grantee", color: blue });
        case PhaseStatus.GRANTEE_PENDING_REVISION:
            return getLateStatus(dueDate, { text: "Need Information - Grantee", color: red });
        case PhaseStatus.PENDING:
            return { text: "Pending Approval - NIH", color: teal };
        case PhaseStatus.APPROVED:
            return { text: isSimpleStatusWorkflow(workflowType) ? "Complete" : "Approved", color: green };
        case PhaseStatus.INACTIVE:
            return { text: "Inactive", color: white, border: "1px solid " + gray };
        default:
            return { text: " *** invalid status *** ", color: black, border: "1px solid " + black };
    }
}

// A task that has reached the end of its workflow - "Approved" on the full status set, "Complete"
// on the simple one, both of which are PhaseStatus.APPROVED. INACTIVE is excluded: it is a task
// that was switched off rather than one that finished, and it is already filtered out of most views.
export const isFinalPhaseStatus = (status?: PhaseStatus | null): boolean =>
    status === PhaseStatus.APPROVED;

const getDaysLate = (dueDate: Date, referenceDate = new Date()): number => {
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.floor((referenceDate.getTime() - dueDate.getTime()) / msPerDay);
}

const getLateStatus = (dueDate: Date | null, status: StatusInfo): StatusInfo => {

    if (!dueDate) return status; // if no due date, no calculation

    const due = typeof dueDate === "string" ? new Date(dueDate) : dueDate;
    const now = new Date();

    // Strip time component for comparison
    const dueStartOfDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (dueStartOfDay >= nowStartOfDay)
        return status;

    const daysLate = getDaysLate(due);

    const late: Pick<StatusInfo, "text" | "color"> =
        daysLate > 60
            ? { text: "Late", color: red }
            : { text: "Slightly Late", color: gold };

    return {
        ...status,
        text: `${late.text}/${status.text}`,
        altColor: late.color
    };
};

export function truncateWithEllipsis(str: string | null, maxLength: number): string | null {
    if (str == null || str.length <= maxLength) return str;
    return str.slice(0, maxLength).trimEnd() + '...';
}
export function resolveTasksFlags(
    mode: TasklistMode = "default",
    overrides?: Partial<TasksFeatureFlags>,
    workflowType?: PhaseWorkflowType | null
): TasksFeatureFlags {
    let base: TasksFeatureFlags;

    if (mode === "templates") {
        base = TEMPLATE_DEFAULTS;
    } else if (workflowType === PhaseWorkflowType.None) {
        base = NONE_WORKFLOW_DEFAULTS;
    } else {
        base = GRANT_DEFAULTS;
    }

    return { ...base, ...(overrides ?? {}) };
}

// ============================================================================
// WORKFLOW PERMISSION HELPERS
// These functions check permissions using the workflow config that comes with
// the task/subtask data, eliminating the need for API calls.
// ============================================================================

/**
 * Check if a user role has a specific permission at a specific step status.
 */
export function hasStepPermission(
    config: StepPermissionConfig | null | undefined,
    userRole: UserRole,
    permission: PermissionType,
    status: StepStatus
): boolean {
    if (!config?.roleConfigs) return false;

    const roleConfig = config.roleConfigs.find(rc => rc.role === userRole);
    if (!roleConfig?.permissions) return false;

    const permEntry = roleConfig.permissions.find(p => p.permission === permission);
    if (!permEntry?.statuses) return false;

    return permEntry.statuses.includes(status);
}

/**
 * Check if a user role has a specific permission at a specific phase status.
 */
export function hasPhasePermission(
    config: PhasePermissionConfig | null | undefined,
    userRole: UserRole,
    permission: PermissionType,
    status: PhaseStatus
): boolean {
    if (!config?.roleConfigs) return false;

    const roleConfig = config.roleConfigs.find(rc => rc.role === userRole);
    if (!roleConfig?.permissions) return false;

    const permEntry = roleConfig.permissions.find(p => p.permission === permission);
    if (!permEntry?.statuses) return false;

    return permEntry.statuses.includes(status);
}

/**
 * Check if a user role has a specific permission at ANY status (ignores current status).
 * Used for STEP workflows where task-level permissions apply regardless of task status.
 */
export function hasPhasePermissionIgnoringStatus(
    config: PhasePermissionConfig | null | undefined,
    userRole: UserRole,
    permission: PermissionType
): boolean {
    if (!config?.roleConfigs) return false;

    const roleConfig = config.roleConfigs.find(rc => rc.role === userRole);
    if (!roleConfig?.permissions) return false;

    const permEntry = roleConfig.permissions.find(p => p.permission === permission);
    if (!permEntry?.statuses) return false;

    // Permission is granted if configured for ANY status (ignores current status)
    return permEntry.statuses.length > 0;
}

/**
 * Check if a user role can perform a specific action at a specific phase status.
 */
export function canPerformPhaseAction(
    config: PhaseWorkflowConfig | null | undefined,
    userRole: UserRole,
    action: PhaseAction,
    status: PhaseStatus
): boolean {
    if (!config?.actions?.actions) return false;

    return config.actions.actions.some(
        a => a.status === status && a.action === action && a.roles?.includes(userRole)
    );
}

/**
 * Check if a user role can transition a step from one status to another.
 */
export function canTransitionStep(
    config: StepTransitionConfig | null | undefined,
    userRole: UserRole,
    fromStatus: StepStatus,
    toStatus: StepStatus
): boolean {
    if (!config?.transitions) return false;

    return config.transitions.some(
        t => t.from === fromStatus && t.to === toStatus && t.roles?.includes(userRole)
    );
}

/**
 * Convenience function to check step permission using workflow config from step data.
 * Handles SuperUser bypass automatically.
 * Falls back to phase's DefaultStepSettings if step doesn't have its own config (matches backend behavior).
 * For external users, resolves their grant-specific role before checking permissions.
 *
 * VIEW PERMISSION AUTO-GRANT:
 * If checking View permission and it's not explicitly configured, the function checks if the role
 * has ANY other permission at the current status. This matches the backend normalization behavior
 * and ensures users can see steps they have permissions for, even if View wasn't explicitly saved.
 *
 * @param step - The step to check permissions for
 * @param app - The app context with user info
 * @param permission - The permission type to check
 * @param defaultIfNoConfig - What to return if no workflow config exists (default: true = allow)
 * @param phase - Optional phase to use for fallback to DefaultStepSettings
 * @param grantId - Grant ID for resolving external user roles (required for external users)
 */
export function checkStepPermission(
    step: StepDto | null | undefined,
    app: IAppContext,
    permission: PermissionType,
    defaultIfNoConfig: boolean = true,
    phase?: PhaseDto | null,
    grantId?: string | null
): boolean {
    // If no step, return default
    if (!step) return defaultIfNoConfig;

    // Resolve effective user role (handles external user -> grant-specific role conversion)
    const effectiveRole = getEffectiveUserRole(app, grantId ?? null);

    // If external user has no role for this grant, deny permission
    if (effectiveRole === null) return false;

    // First try step's own config, then fall back to phase's DefaultStepSettings (matches backend)
    const workflow = step.defaultStep?.settings?.workflow
        ?? phase?.defaultPhase?.settings?.defaultStepSettings?.workflow
        ?? null;
    if (!workflow?.permissions) return defaultIfNoConfig;

    // For None workflow, treat NOT_STARTED as SETUP for permission checking
    // None workflow only has 2 conceptual states (In Progress and Complete), but steps may
    // be created at NOT_STARTED. This normalization allows them to have the same permissions as SETUP.
    const workflowType = phase?.defaultPhase?.settings?.workflowType ?? null;
    let effectiveStatus = step.status;
    if (workflowType === PhaseWorkflowType.None && step.status === StepStatus.NOT_STARTED) {
        effectiveStatus = StepStatus.SETUP;
    }

    // Check if the permission is explicitly configured
    const hasExplicitPermission = hasStepPermission(workflow.permissions, effectiveRole, permission, effectiveStatus);
    if (hasExplicitPermission) return true;

    // VIEW PERMISSION FALLBACK:
    // If checking View permission and it's not explicitly configured, check if the role has
    // ANY other permission at this status. This matches backend normalization logic and ensures
    // users can see steps they have permissions for, even if data hasn't been re-saved.
    if (permission === PermissionType.View) {
        const roleConfig = workflow.permissions.roleConfigs?.find(rc => rc.role === effectiveRole);
        if (roleConfig?.permissions) {
            // Check if any permission (other than View) is configured for this status
            for (const permEntry of roleConfig.permissions) {
                if (permEntry.permission !== PermissionType.View && permEntry.statuses?.includes(effectiveStatus)) {
                    return true; // Has another permission at this status, so View is auto-granted
                }
            }
        }
    }

    return false;
}

/**
 * Convenience function to check phase permission using workflow config from phase data.
 * For external users, resolves their grant-specific role before checking permissions.
 *
 * STEP WORKFLOW NOTE: For STEP workflows, task-level permissions ignore the current status.
 * If the role has the permission configured for ANY status, they have the permission.
 * This is because in STEP workflows, detailed permissions are controlled at the subtask level.
 *
 * @param defaultIfNoConfig - What to return if no workflow config exists (default: true = allow)
 * @param grantId - Grant ID for resolving external user roles (required for external users)
 */
export function checkPhasePermission(
    phase: PhaseDto,
    app: IAppContext,
    permission: PermissionType,
    defaultIfNoConfig: boolean = true,
    grantId?: string | null
): boolean {
    // Resolve effective user role (handles external user -> grant-specific role conversion)
    const effectiveRole = getEffectiveUserRole(app, grantId ?? null);

    // If external user has no role for this grant, deny permission
    if (effectiveRole === null) return false;

    const workflow = phase.defaultPhase?.settings?.workflow;
    if (!workflow?.permissions) return defaultIfNoConfig;

    // Step-style workflows (Step or StepHybrid) have no task-level status, so task
    // permissions apply regardless of status. Subtask permissions still depend on status.
    const workflowType = phase.defaultPhase?.settings?.workflowType;
    if (isStepStyleWorkflow(workflowType)) {
        return hasPhasePermissionIgnoringStatus(workflow.permissions, effectiveRole, permission);
    }

    return hasPhasePermission(workflow.permissions, effectiveRole, permission, phase.status);
}

/**
 * Convenience function to check if user can transition step to a new status.
 * For external users, resolves their grant-specific role before checking permissions.
 * @param defaultIfNoConfig - What to return if no workflow config exists (default: true = allow)
 * @param phase - Optional phase for fallback to DefaultStepSettings workflow config
 * @param grantId - Grant ID for resolving external user roles (required for external users)
 */
export function checkStepTransition(
    step: StepDto,
    app: IAppContext,
    toStatus: StepStatus,
    defaultIfNoConfig: boolean = true,
    phase?: PhaseDto | null,
    grantId?: string | null
): boolean {
    // Resolve effective user role (handles external user -> grant-specific role conversion)
    const effectiveRole = getEffectiveUserRole(app, grantId ?? null);

    // If external user has no role for this grant, deny permission
    if (effectiveRole === null) return false;

    const workflow = step.defaultStep?.settings?.workflow
        ?? phase?.defaultPhase?.settings?.defaultStepSettings?.workflow
        ?? null;
    if (!workflow?.transitions) return defaultIfNoConfig;

    const fromStatus = step.status;

    return canTransitionStep(workflow.transitions, effectiveRole, fromStatus, toStatus);
}

// VIEW PERMISSION HELPERS

/**
 * Check if response content is available at this status.
 * This is a pure data availability check - response data doesn't exist before GRANTEE_PENDING.
 * This is NOT a permission check and should remain status-based.
 */
export function canViewResponseContent(status: StepStatus): boolean {
    return status >= StepStatus.GRANTEE_PENDING;
}

export function isHybridParentType(t?: PhaseWorkflowType | null): boolean {
    return t === PhaseWorkflowType.PhaseHybrid || t === PhaseWorkflowType.StepHybrid;
}

// True for task types that have no overall task-level status (subtasks run independently).
// Used to suppress the parent status icon and summary status label.
export function isStepStyleWorkflow(t?: PhaseWorkflowType | null): boolean {
    return t === PhaseWorkflowType.Step || t === PhaseWorkflowType.StepHybrid;
}

// Mirrors DefaultPhase.GetEffectiveStepType on the backend.
// Non-hybrid task: every subtask inherits the task's type, and whatever the subtask itself stores
// is ignored - only a hybrid task lets a subtask declare its own (falling back to Step when unset).
// Pass a null step to ask what a NEW subtask under this task would get.
export function getEffectiveStepType(phase: DefaultPhaseDto, step: DefaultStepDto | null): StepWorkflowType {
    if (isHybridParentType(phase.settings?.workflowType)) {
        return step?.settings?.workflowType ?? StepWorkflowType.Step;
    }
    switch (phase.settings?.workflowType) {
        case PhaseWorkflowType.Phase: return StepWorkflowType.Phase;
        case PhaseWorkflowType.Step: return StepWorkflowType.Step;
        case PhaseWorkflowType.None: return StepWorkflowType.None;
        default: return StepWorkflowType.Step;
    }
}

// Whether a subtask can use configured documents. Only a subtask with a single file area can:
// the document list names one file per document, and a subtask with separate Request and Response
// areas has two places a document could live with no way to say which one is meant. That is exactly
// the None step type - every other type carries the request/response pair.
export function takesConfiguredDocuments(t?: StepWorkflowType | null): boolean {
    return t === StepWorkflowType.None;
}

// Maps a resolved StepWorkflowType to the equivalent PhaseWorkflowType so existing
// tabs and helpers that key off PhaseWorkflowType keep working under a hybrid parent.
export function stepToPhaseWorkflowType(t: StepWorkflowType): PhaseWorkflowType {
    switch (t) {
        case StepWorkflowType.Phase: return PhaseWorkflowType.Phase;
        case StepWorkflowType.Step: return PhaseWorkflowType.Step;
        case StepWorkflowType.None: return PhaseWorkflowType.None;
    }
}
// Text stripped of markup and entities, used to tell "has visible text" from "empty markup".
export const htmlToPlainText = (html: string | null | undefined): string =>
    (html ?? "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

// Trims a rich-text value. The editor wraps content in markup, so leading and trailing
// whitespace lives inside empty blocks and line breaks rather than at the ends of the
// string - a plain trim() would leave all of it. A value with no visible text becomes "".
export const trimHtmlText = (html: string | null | undefined): string => {
    const value = (html ?? "").trim();
    if (!value) return "";
    if (!htmlToPlainText(value)) return "";

    const blank = "(?:\s|<br\s*\/?>|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>|<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>)+";
    return value
        .replace(new RegExp("^" + blank, "i"), "")
        .replace(new RegExp(blank + "$", "i"), "")
        .trim();
};

// Trims the free-text a user typed into a subtask before it is saved. Covers the plain-text
// description and the request/response rich-text bodies; other fields are left untouched.
export function trimStepText(step: StepDto): StepDto {
    const trimmed: StepDto = {
        ...step,
        name: step.name?.trim() ?? step.name,
        description: step.description?.trim() ?? step.description
    };

    if (step.requestComment) {
        trimmed.requestComment = { ...step.requestComment, comment: trimHtmlText(step.requestComment.comment) };
    }
    if (step.responseComment) {
        trimmed.responseComment = { ...step.responseComment, comment: trimHtmlText(step.responseComment.comment) };
    }

    return trimmed;
}


// Overall task status for Step-style tasks (Step and Step Hybrid). These have no task-level
// workflow of their own - each subtask runs independently - so the indicator is derived from the
// subtasks in view rather than the stored phase status, which only tracks the coarse task-level
// transitions.
//
// The task reports the least advanced subtask that has started, so releasing one subtask moves the
// task off Not Started instead of being held back by the siblings still waiting to be released.
// The task is only complete once every subtask is.
export function deriveStepStyleTaskStatus(steps: StepDto[]): PhaseStatus {
    if (!steps || steps.length === 0) return PhaseStatus.NOT_STARTED;

    // A subtask switched off is not something the task is waiting on. A task whose subtasks are
    // all switched off is itself inactive.
    const active = steps.filter(s => s.status !== StepStatus.INACTIVE);
    if (active.length === 0) return PhaseStatus.INACTIVE;

    // Complete only when nothing is left, including subtasks nobody has released yet.
    if (active.every(s => s.status === StepStatus.APPROVED)) return PhaseStatus.APPROVED;

    const started = active.filter(s => s.status !== StepStatus.NOT_STARTED);
    if (started.length === 0) return PhaseStatus.NOT_STARTED;

    const least = started.reduce((lowest, s) => (s.status < lowest ? s.status : lowest), started[0].status);

    // Everything released so far is approved, but subtasks remain for NIH to release.
    if (least === StepStatus.APPROVED) return PhaseStatus.SETUP;

    return stepStatusToPhaseStatus(least);
}

// The two status sets share their values apart from the states that only exist per subtask.
function stepStatusToPhaseStatus(status: StepStatus): PhaseStatus {
    switch (status) {
        // The grantee has finished and it sits with NIH - at task level that reads as Pending.
        case StepStatus.GRANTEE_COMPLETE: return PhaseStatus.PENDING;
        // A task has one revision status where a subtask has two; both read the same to the user.
        case StepStatus.PENDING_REVISION: return PhaseStatus.GRANTEE_PENDING_REVISION;
        default: return status as number as PhaseStatus;
    }
}
